import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Target, FileCheck2, Eye, GitBranch, ArrowRight } from "lucide-react";
import Seo from "@/components/Seo";

const PILLARS = [
  { Icon: Target, title: "Precision", body: "We select materials by chemical identity and specification, and label every shipment against its order — no substitutions, no approximations." },
  { Icon: FileCheck2, title: "Documentation", body: "Where documentation is provided, it is made available on the product page and in our Documentation Center. We publish what we have and never fabricate records." },
  { Icon: Eye, title: "Transparency", body: "Clear specifications, clear policies, and a clear Research Use Only stance. We describe what our materials are — and what they are not." },
  { Icon: GitBranch, title: "Traceability", body: "Inventory is tracked at the vial level, and batch documentation can be verified by lot number where it is on record." },
];

export default function About() {
  return (
    <main className="pt-32 pb-28" data-testid="about-page">
      <Seo title="About Molecularix Biolabs" path="/about"
        description="Molecularix Biolabs LLC supplies reference-grade peptides and research chemicals for in-vitro laboratory research. Learn about our mission, quality philosophy, and Research Use Only commitment." />
      <div className="max-w-4xl mx-auto px-6 md:px-12">
        <motion.p initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }}
          className="font-mono text-[11px] uppercase tracking-[0.3em] text-[#D4AF37] mb-5">About</motion.p>
        <h1 className="font-serif text-4xl sm:text-5xl md:text-6xl font-light leading-[1.02] mb-6">
          A research-first<br />materials supplier.
        </h1>
        <p className="text-[#A1A1AA] text-base md:text-lg leading-relaxed max-w-2xl mb-6">
          Molecularix Biolabs LLC supplies reference-grade peptides and research chemicals for in-vitro laboratory research. We are not a pharmacy, clinical laboratory, or pharmaceutical manufacturer — our focus is sourcing, documentation, careful handling, and disciplined compliance.
        </p>
        <p className="text-[#A1A1AA] text-base leading-relaxed max-w-2xl mb-14">
          Our aim is simple: give researchers a catalog they can trust, with clear specifications, honest documentation, and a positioning that never strays from Research Use Only.
        </p>

        <div className="grid sm:grid-cols-2 gap-px bg-zinc-900 border border-zinc-900 mb-14">
          {PILLARS.map(({ Icon, title, body }, i) => (
            <motion.div key={title} initial={{ opacity: 0, y: 16 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
              transition={{ duration: 0.5, delay: (i % 2) * 0.08 }} className="bg-[#0A0A0A] p-8 md:p-10" data-testid={`about-pillar-${i}`}>
              <Icon size={20} className="text-[#D4AF37] mb-5" />
              <h2 className="font-serif text-2xl font-light mb-3">{title}</h2>
              <p className="text-[#A1A1AA] text-sm leading-relaxed">{body}</p>
            </motion.div>
          ))}
        </div>

        <div className="grid sm:grid-cols-2 gap-8 mb-14">
          <div>
            <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#52525B] mb-3">Company</p>
            <p className="text-[#A1A1AA] text-sm leading-relaxed">
              Molecularix Biolabs LLC<br />205 Carpenter Street<br />Muncy, PA 17756<br />United States
            </p>
          </div>
          <div>
            <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#52525B] mb-3">Contact</p>
            <p className="text-[#A1A1AA] text-sm leading-relaxed">
              MolecularixBiolabs@gmail.com<br />
              <Link to="/contact" className="text-[#D4AF37] hover:underline">Contact support →</Link>
            </p>
          </div>
        </div>

        <div className="border border-[#D4AF37]/25 bg-[#D4AF37]/[0.04] p-8 mb-14" data-testid="about-ruo">
          <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#D4AF37] mb-3">Research Use Only</p>
          <p className="text-[#71717A] text-sm leading-relaxed">
            All products offered by Molecularix Biolabs LLC are intended strictly for in-vitro research use only. They are not drugs, foods, cosmetics, or supplements, and are not for human or veterinary use. We do not provide dosing, administration, or usage guidance.
          </p>
        </div>

        <div className="pt-8 border-t border-zinc-900 flex flex-wrap gap-4">
          <Link to="/" className="inline-flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.2em] text-[#D4AF37] hover:underline">Browse the catalog <ArrowRight size={13} /></Link>
          <Link to="/standards" className="font-mono text-[11px] uppercase tracking-[0.2em] text-[#A1A1AA] hover:text-[#D4AF37] transition-colors">Our standards</Link>
        </div>
      </div>
    </main>
  );
}
